import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InfoButton, type InfoTopic } from "@/components/info-button";

/**
 * "Required piping: Sch 80 PVC outside, copper inside" — one switch on the
 * customer's Pricing card (migration 701, Corbin 2026-10-28). FWISD and similar
 * schools require a set pipe when we replace a device 2" and smaller.
 *
 * Self-contained on purpose: its own read and its own save
 * (`/api/customers/:id/piping-rule`), so it sits in the card as one row and
 * never touches the card's pricing save.
 */
export const PIPING_RULE_HELP: InfoTopic = {
    title: "Required piping",
    lines: [
        "For 2-inch and smaller replacements.",
        "Inside above ground gets copper. Outside with any PVC gets Sch 80 PVC.",
        "If the device doesn't say inside or outside, or what pipe it has, the quote gets a line that asks you to pick: Copper or Sch 80 PVC. One tap fixes it.",
        "Off = we put back the pipe the device already has.",
    ],
};

export const PIPING_RULE_LABEL = "Required piping: Sch 80 PVC outside, copper inside";

export function CustomerPipingRuleRow({ customerId }: { customerId: string }) {
    const qc = useQueryClient();
    const key = ["customer-piping-rule", customerId];
    const { data, isLoading } = useQuery({
        queryKey: key,
        queryFn: () => api.get<{ on: boolean }>(`/api/customers/${customerId}/piping-rule`),
        enabled: !!customerId,
    });
    const save = useMutation({
        mutationFn: (on: boolean) => api.put<{ on: boolean }>(`/api/customers/${customerId}/piping-rule`, { on }),
        onSuccess: async (r) => {
            qc.setQueryData(key, r);
            toast.success(r.on ? "Piping rule on — new replacement quotes follow it" : "Piping rule off");
        },
        onError: (e: any) => {
            qc.invalidateQueries({ queryKey: key });
            toast.error(`NOT saved: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`);
        },
    });

    const on = data?.on === true;
    const busy = isLoading || save.isPending;
    return (
        <div
            className="mt-3 flex items-center gap-3 rounded-lg border border-border/60 bg-card px-3.5 py-2.5"
            data-testid="customer-piping-rule"
        >
            <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={PIPING_RULE_LABEL}
                disabled={busy}
                onClick={() => save.mutate(!on)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-wait"
            >
                <span
                    className={cn(
                        "relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors",
                        on ? "bg-emerald-600" : "bg-muted-foreground/50",
                    )}
                >
                    <span
                        className={cn(
                            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-[left]",
                            on ? "left-[22px]" : "left-0.5",
                        )}
                    />
                </span>
                <span className="min-w-0">
                    <span className="block text-sm font-semibold">{PIPING_RULE_LABEL}</span>
                    <span className="block text-xs text-muted-foreground">
                        {on ? "On" : "Off"} · replacements 2&quot; and smaller
                    </span>
                </span>
            </button>
            <InfoButton topic={PIPING_RULE_HELP} />
        </div>
    );
}
